import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import { useClassName } from 'common/hooks';
import { SVGIcon as Icon } from 'common/components';

import './character-appearances.less';

const timeToAmPm = (time) => {
  return DateTime.fromFormat(time, 'HH:mm', {
    zone: 'America/New_York',
  }).toFormat('h:mm a');
};

const CharacterAppearances = ({ appearances, title, mode, onDetails }) => {
  const classNames = useClassName('CharacterAppearances');
  const [visible, setVisible] = useState(false);
  const isCta = mode === 'cta';

  if (!isCta && !appearances?.length) return null;

  const grouped = Object.entries(
    appearances.reduce((acc, curr) => {
      if (!acc[curr.startTime]) acc[curr.startTime] = [];
      acc[curr.startTime].push(curr);
      return acc;
    }, {})
  ).sort(([timeA], [timeB]) => timeA.localeCompare(timeB));

  return (
    <div className={classNames(['container', isCta ? 'cta' : ''])}>
      <p
        onClick={() => (isCta ? onDetails?.() : setVisible(!visible))}
        className={classNames('title')}>
        <Icon name="drama" className={classNames('title__icon')} size={24} />
        <span>{title}</span>
        {isCta ? (
          <span
            className={classNames('cta-link')}
            onClick={(e) => {
              e.stopPropagation();
              onDetails?.();
            }}>
            Details
          </span>
        ) : (
          <Icon
            name={visible ? 'angle-up' : 'angle-down'}
            className={classNames('title__arrow')}
          />
        )}
      </p>
      {!isCta && visible && (
        <div className={classNames('block')}>
          {grouped.map(([time, items]) => (
            <div key={time} className={classNames('block__item')}>
              <p className={classNames('block__item__date')}>
                {timeToAmPm(time)}
              </p>
              <div className={classNames('block__item__characters')}>
                {items.map((item, idx) => (
                  <div
                    key={`${item.character?.id}-${idx}`}
                    className={classNames('block__item__characters__item')}>
                    {item.character?.image && (
                      <img
                        src={item.character?.image}
                        alt={item.character?.name}
                        className={classNames(
                          'block__item__characters__item__image'
                        )}
                      />
                    )}
                    <div
                      className={classNames(
                        'block__item__characters__item__info'
                      )}>
                      <p
                        className={classNames(
                          'block__item__characters__item__name'
                        )}>
                        {item.character?.name}
                      </p>
                      <p
                        className={classNames(
                          'block__item__characters__item__location'
                        )}>
                        <Icon
                          name="location-pin"
                          className={classNames(
                            'block__item__characters__item__location__icon'
                          )}
                        />
                        <span>{item.location?.name}</span>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

CharacterAppearances.propTypes = {
  appearances: PropTypes.array,
  title: PropTypes.string,
  // 'toggle' (default): expandable accordion. 'cta': static row with a
  // "Details" link that calls onDetails (used below the checkout date cards).
  mode: PropTypes.oneOf(['toggle', 'cta']),
  onDetails: PropTypes.func,
};

CharacterAppearances.defaultProps = {
  appearances: [],
  title: 'Characters Appearances',
  mode: 'toggle',
  onDetails: null,
};

export default CharacterAppearances;
